import React, { useEffect } from 'react';
import { Formik, Form as FForm, useFormikContext } from 'formik';
import {
    Button,
    FormInput,
    FormSelect,
    FormCheckbox,
    FormInlineDatepicker,
    FormImageInput,
    FormInputList,
    FormEditor,
    FormSelectList,
    FormLabel,
    Text
} from './';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import DateTimePicker from 'react-datetime-picker'


const FormikOnChangeComponent = props => {
    const { values } = useFormikContext();
    useEffect(() => {
        props.onChange(values);
    }, [values]);

    return null;
};

FormikOnChangeComponent.protoTypes = {
    props: PropTypes.object.isRequired
};

/**
 * Lists everything that is blocking a save, once the user has tried to submit.
 *
 * Some inputs cannot show an error next to themselves at all - the image picker never
 * marks itself touched, and the list/select-list inputs render no error slot - so without
 * this a form could refuse to save while showing the user nothing at all.
 */
const FormValidationSummary = ({ errors, submitCount, fields }) => {

    const keys = Object.keys(errors || {}).filter(key => errors[key]);

    if (!submitCount || !keys.length) {
        return null;
    }

    const labelFor = key => {
        const field = (fields || []).find(f => f.name === key);
        return field && field.label ? field.label : null;
    };

    //Yup errors are strings or FormattedMessage elements for plain fields, but for array
    //fields with an item schema they can be an array or an object keyed by index. React
    //throws on a plain object child, so anything that is not renderable is flattened to text.
    const renderError = value => {
        if (value === null || value === undefined) {
            return null;
        }

        if (typeof value === 'string' || React.isValidElement(value)) {
            return value;
        }

        if (Array.isArray(value)) {
            return value.filter(Boolean).map((v, idx) => <span key={idx}>{idx > 0 ? ', ' : ''}{renderError(v)}</span>);
        }

        if (typeof value === 'object') {
            return Object.keys(value).map((k, idx) => <span key={k}>{idx > 0 ? ', ' : ''}{renderError(value[k])}</span>);
        }

        return String(value);
    };

    return (
        <div className="rounded-md border border-red-300 bg-red-50 p-3" role="alert">
            <Text left small className="text-red-700 font-medium" label="sv.form.validation_summary.title" />
            <ul className="mt-1 list-disc list-inside">
                {keys.map(key => (
                    <li key={key} className="text-red-700 text-sm text-left">
                        {labelFor(key) ? <Text small className="text-red-700" label={labelFor(key)} /> : key}
                        :&nbsp;
                        {renderError(errors[key])}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export const Form = props => {

    return(
        <Formik
            enableReinitialize={props.enableReinitialize}
            initialValues={props.initValues}
            validationSchema={Yup.object().shape(props.validationSchema)}
            onSubmit={props.onSubmit}
        >
            {({ isSubmitting, dirty, isValid, setFieldValue, values, submitForm, errors, submitCount }) => (
                <FForm autoComplete="off" className={'space-y-6 ' + (props.className || '')}>
                    {props.onChange &&
                        <FormikOnChangeComponent onChange={props.onChange} />
                    }
                    {props.submitWhenInvalid &&
                        <FormValidationSummary errors={errors} submitCount={submitCount} fields={props.fields} />
                    }
                    <div className="space-y-2">
                        {props.fields.map((field, idx) => {
                                switch (field.type) {
                                    case 'checkbox':
                                        return <FormCheckbox key={idx} {...field} />;
                                    case 'select':
                                        return <FormSelect key={idx} {...field} />;
                                    case 'inlineDate':
                                        return <FormInlineDatepicker
                                            key={idx}
                                            {...field}
                                            onChange={setFieldValue}
                                            date={values[field.name]} />;
                                    case 'dateTimePicker':
                                        return <>
                                            {field.label && <FormLabel label={field.label} name={field.name}/>}
                                            <DateTimePicker
                                              name={field.name}
                                              returnValue="start"
                                              onChange={(val) => {field.onChange(val); setFieldValue(field.name, val)}}
                                              value={field.value}
                                              format={field.format ? field.format : ''}
                                              disableClock={true}
                                              required={true}
                                              minDate={field.minDate}
                                              maxDate={field.maxDate}
                                              className="mb-40"
                                            />
                                        </>
                                    case 'image':
                                        return <FormImageInput key={idx} {...field} onChange={setFieldValue} />;
                                    case 'textarea':
                                        return <FormInput key={idx} tag="textarea" {...field} />;
                                    case 'input_list':
                                        return <FormInputList key={idx} {...field} onChange={setFieldValue} />;
                                    case 'select_list':
                                        return <FormSelectList key={idx} {...field} onChange={setFieldValue} />;
                                    case 'editor':
                                        return <FormEditor key={idx} {...field} onChange={setFieldValue}
                                                           initValue={props.initValues[field.name]}
                                        />;
                                    case 'inline_fields':
                                        if (!field.root) {
                                            return null;
                                        }

                                        return (
                                                <div className="w-full">
                                                    <div className={'h-auto ' + (field.labelPosition && field.labelWidth ? field.labelPosition === 'left' ? 'order-1 ' + field.labelWidth : 'order-2 :' + field.labelWidth : '')}>
                                                        <FormLabel label={field.label} name={field.name} labelPosition={field.labelPosition} />
                                                    </div>
                                                    <div className="flex">
                                                        <FormInput className="inline-block" noLabel={true} noError={true} key={idx} {...field} type="text" onChange={setFieldValue} />
                                                        {props.fields.filter(f => f.type === 'inline_fields' && f.relatedTo === field.name)
                                                            .map((f, fIdx) => {
                                                                return (
                                                                    <>
                                                                        {f.slashBefore &&
                                                                            <div className="inline-block ml-2 mt-2">/</div>
                                                                        }
                                                                        <FormInput className="inline-block ml-2" key={fIdx} {...f} noLabel={true} noError={true}
                                                                               type="text" onChange={setFieldValue} />
                                                                    </>
                                                                );
                                                            })
                                                        }
                                                        {(props.errors && props.errors[field.name]) ?
                                                            <Text left small className="text-red-500 sm:text-sm">{props.errors[field.name]}</Text>
                                                            :
                                                            <></>
                                                        }
                                                    </div>
                                                </div>
                                        );
                                    default:
                                        return <div>
                                            <FormInput key={idx} {...field} onChange={setFieldValue} />
                                            {(props.errors && props.errors[field.name]) ?
                                              <Text left small className="text-red-500 sm:text-sm">{props.errors[field.name]}</Text>
                                              :
                                              <></>
                                            }
                                        </div>
                                }
                            }
                        )}
                    </div>
                    {(props.dataBeforeButton ?
                        <>
                            {props.dataBeforeButton}
                        </> : <></>
                    )}
                    <div className={'flex space-x-5 ' + (props.cancelBtn ? 'justify-between ' : '')  + (props.submitBtnClass ? props.submitBtnClass : '')}>
                        {!props.noSubmitBtn ?
                            <Button type={props.noSubmitButton ? "button" : "submit"}
                                    {...(props.noSubmitButton ? {onClick: submitForm} : undefined)}
                                    icon={props.submitBtnIcon} label={props.submitBtnLabel}
                                    small={props.submitBtnSmall}
                                    secondary={props.submitBtnSecondary}
                                    /* submitWhenInvalid keeps the button clickable while the form is invalid,
                                       so submitting marks every field touched and the summary above can
                                       explain what is wrong. Without it a form with an error on an input
                                       that has no error slot is simply dead. */
                                    disabled={props.ignoreValidation && isSubmitting ? true :
                                        props.ignoreValidation ? false :
                                            props.submitWhenInvalid ? (isSubmitting || !dirty) :
                                                isSubmitting || !dirty || !isValid}
                                    className={(props.submitBtnFullWidth ? 'w-full' : '')}/>
                            : <></>
                        }
                        {props.cancelBtn &&
                            <Button label={props.cancelBtnLabel} small={props.submitBtnSmall} onClick={props.cancelBtn}
                                    disabled={isSubmitting} secondary />
                        }
                    </div>
                </FForm>
            )}
        </Formik>
    );

};

Form.propTypes = {
    initValues: PropTypes.object.isRequired,
    validationSchema: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
    submitBtnIcon: PropTypes.string,
    submitBtnLabel: PropTypes.string.isRequired,
    submitBtnClass: PropTypes.string,
    enableReinitialize: PropTypes.bool,
    className: PropTypes.string,
    submitBtnFullWidth: PropTypes.bool,
    cancelBtn: PropTypes.func,
    cancelBtnLabel: PropTypes.string,
    onChange: PropTypes.func,
    noSubmitBtn: PropTypes.bool,
    noSubmitButton: PropTypes.bool,
    ignoreValidation: PropTypes.bool,
    //allow submitting an invalid form so the validation summary can explain what is missing
    submitWhenInvalid: PropTypes.bool
};
